// SocialSideBar Component
// Floating social media buttons that stick to the viewport on the right side

import React, { useState, useEffect, useRef } from 'react';
import './SocialSideBar.css';
import { getSocialLinksForSidebar } from '../utils/socialLinksHelper';
import SidebarButton from './SidebarButton';

function SocialSideBar() {
  const [isHidden, setIsHidden] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const intersectionStates = useRef({ socials: false, footer: false });
  const isInitialized = useRef(false);
  
  // Get social links for sidebar
  const socialLinks = getSocialLinksForSidebar();

  // Toggle mobile dropdown
  const toggleMobileDropdown = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  useEffect(() => {
    // Delay observer setup to ensure DOM is fully rendered
    const setupObserver = () => {
      // Use Intersection Observer to detect when socials or footer sections are in view
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1 // Trigger when 10% of the section is visible
      };

      const observerCallback = (entries) => {
        // Only process if initialized (prevents immediate hiding on load)
        if (!isInitialized.current) {
          return;
        }

        entries.forEach(entry => {
          if (entry.target.classList.contains('footer')) {
            intersectionStates.current.footer = entry.isIntersecting;
          }
        });

        // Hide sidebar if footer is in view (only after initialization)
        setIsHidden(intersectionStates.current.footer);
      };

      const observer = new IntersectionObserver(observerCallback, observerOptions);

        // Observe both socials and footer sections with a delay
        // This prevents immediate hiding on page load
        setTimeout(() => {
          isInitialized.current = true;
          
          const footerSection = document.querySelector('footer.footer');

          if (footerSection) {
            observer.observe(footerSection);
          }
        }, 1000); // Delay to prevent immediate hiding on load

      // Store observer for cleanup
      let observerInstance = observer;
      
      // Cleanup observer on unmount
      return () => {
        if (observerInstance) {
          observerInstance.disconnect();
        }
      };
    };

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', setupObserver);
      return () => {
        document.removeEventListener('DOMContentLoaded', setupObserver);
      };
    } else {
      const cleanup = setupObserver();
      return cleanup;
    }
  }, []);

  // Close mobile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileOpen) {
        const toggleButton = document.querySelector('.social-mobile-toggle');
        const dropdown = document.querySelector('.social-mobile-dropdown');
        
        if (toggleButton && dropdown && 
            !toggleButton.contains(event.target) && 
            !dropdown.contains(event.target)) {
          setIsMobileOpen(false);
        }
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOpen]);

  return (
    <>
      {/* Mobile dropdown toggle button */}
      <SidebarButton
        iconClass="fas fa-share-alt"
        className="social-mobile-toggle"
        onClick={toggleMobileDropdown}
        ariaLabel="Toggle social links"
      />

      {/* Desktop sidebar - hidden on mobile */}
      <div className={`social-sidebar-container ${isHidden ? 'hidden' : ''}`}>
        {socialLinks.map((social, index) => (
          <SidebarButton
            key={index}
            icon={social.icon}
            iconClass={social.iconClass}
            isText={social.isText}
            className={social.className}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            ariaLabel={social.name}
          />
        ))}
      </div>

      {/* Mobile dropdown menu */}
      <div className={`social-mobile-dropdown ${isMobileOpen ? 'open' : ''}`}>
        {socialLinks.map((social, index) => (
          <SidebarButton
            key={index}
            icon={social.icon}
            iconClass={social.iconClass}
            isText={social.isText}
            className={`social-mobile-button ${social.className}`}
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            ariaLabel={social.name}
            onClick={() => setIsMobileOpen(false)}
          />
        ))}
      </div>
    </>
  );
}

export default SocialSideBar;

